import { useState, useMemo } from 'react';
import { IPIP_NEO_FACETS } from '../../content/personaContent';
import HelpTooltip from '../atoms/HelpTooltip';
import BottomSheet from '../atoms/BottomSheet';
import styles from './Initializer.module.css';

// Main personality trait editor with full slider control
function SlidersStep({
  personaData,
  setPersonaData,
  onNext,
  onBack,
  currentStepNumber,
  totalSteps,
}) {
  const defaultState = useMemo(() => {
    const state = {};
    Object.keys(IPIP_NEO_FACETS).forEach((domainKey) => {
      state[domainKey] = {};
      Object.keys(IPIP_NEO_FACETS[domainKey].facets).forEach((facetKey) => {
        state[domainKey][facetKey] = 50;
      });
    });
    return state;
  }, []);

  const [formData, setFormData] = useState(
    personaData.personalityData || defaultState
  );
  const [activeTooltip, setActiveTooltip] = useState(null);

  const handleChange = (e, domainKey, facetKey) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [domainKey]: {
        ...prev[domainKey],
        [facetKey]: parseInt(value, 10),
      },
    }));
  };

  const handleNext = () => {
    const finalPersonalityData = { ...formData };

    // Calculate the average score for each domain from its facets
    Object.keys(IPIP_NEO_FACETS).forEach((domainKey) => {
      const domainFacets = formData[domainKey];
      if (domainFacets) {
        const facetScores = Object.values(domainFacets);
        const averageScore = Math.round(
          facetScores.reduce((a, b) => a + b, 0) / facetScores.length
        );
        finalPersonalityData[`${domainKey}Score`] = averageScore;
      }
    });

    setPersonaData((prevData) => ({
      ...prevData,
      personalityData: finalPersonalityData,
    }));
    onNext();
  };

  return (
    <div>
      <h3>
        Step {currentStepNumber}/{totalSteps}: Sculpt the Personality
      </h3>
      <p>
        Adjust the 30 personality facets that form the core of your persona's
        character. These scores (1-100) will influence their behavior,
        decisions, and style.
      </p>

      {Object.entries(IPIP_NEO_FACETS).map(([domainKey, domainData]) => (
        <fieldset key={domainKey} className={styles.formSection}>
          <legend>{domainData.title}</legend>
          {Object.entries(domainData.facets).map(([facetKey, facetData]) => (
            <div key={facetKey} className={styles.sliderGroup}>
              <label htmlFor={facetKey}>
                {facetData.name}
                <HelpTooltip
                  content={facetData.description}
                  onMobileClick={setActiveTooltip}
                />
              </label>
              <input
                type="range"
                id={facetKey}
                name={facetKey}
                min="1"
                max="100"
                value={formData[domainKey]?.[facetKey] || 50}
                onChange={(e) => handleChange(e, domainKey, facetKey)}
              />
              <span className={styles.sliderValue}>
                {formData[domainKey]?.[facetKey] || 50}
              </span>
            </div>
          ))}
        </fieldset>
      ))}

      <div className={styles.buttonGroup}>
        <button onClick={onBack} className="action-button secondary">
          Back
        </button>
        <button onClick={handleNext} className="action-button primary">
          Next
        </button>
      </div>

      {/* Tooltip display area on mobile. */}
      <BottomSheet
        isOpen={!!activeTooltip}
        onClose={() => setActiveTooltip(null)}
      >
        {activeTooltip}
      </BottomSheet>
    </div>
  );
}

export default SlidersStep;
